/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState, useEffect, useMemo } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";

// MUI components
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import Icon from "@mui/material/Icon";

// Dashboard components
import MDBox from "components/MDBox";
import Sidenav from "examples/Sidenav";
import Configurator from "examples/Configurator";

// Themes
import theme from "assets/theme";
import themeRTL from "assets/theme/theme-rtl";
import themeDark from "assets/theme-dark";
import themeDarkRTL from "assets/theme-dark/theme-rtl";

// RTL setup
import rtlPlugin from "stylis-plugin-rtl";
import { CacheProvider } from "@emotion/react";
import createCache from "@emotion/cache";

// Routes & context
import routes from "routes";
import {
  useMaterialUIController,
  setMiniSidenav,
  setOpenConfigurator,
} from "context";

// Chatbot components
import { ChatbotProvider } from "context/ChatbotContext";
import SimpleAIChatbot from "components/AIChatbot/SimpleAIChatbot";
import ChatbotButton from "components/AIChatbot/ChatbotButton";

// Assets
import brandWhite from "assets/images/logo-ct.png";
import brandDark from "assets/images/logo-ct-dark.png";

// Notifications
import SafeToastContainer from "components/SafeToastContainer";
import "react-toastify/dist/ReactToastify.css";

// Error handling
import ErrorBoundary from "components/ErrorBoundary";
import { initializeGlobalErrorHandlers } from "utils/globalErrorHandler";

// Temporary pages
import ComingSoon from "layouts/ComingSoon"; // Corrected spelling if necessary
import Profile from "layouts/authentication/components/profile";
import SignIn from "layouts/authentication/sign-in";
import SignUp from "layouts/authentication/sign-up";
import CheckingAuth from "layouts/authentication/components/CheckingAuth";
import BasicDetails from "layouts/profile/components/BasicInfo";
import DistrictStudentMap from "layouts/profile/components/DistrictStudentMap";
import ChapterDetails from "layouts/profile/components/ChapterList";
import AdminAuth from "layouts/authentication/components/AdminAuth";
import AdminLogin from "layouts/authentication/components/AdminLogin";
import AdminRegister from "layouts/authentication/components/AdminRegister";
import AdminHome from "layouts/authentication/components/AdminHome";
import AdminChapterManager from "./layouts/authentication/components/AdminChapterManager";
import LinkedInIntegration from "./layouts/profile/components/LinkedInIntegration";
import GlobalChat from "./layouts/profile/components/GlobalChat";
import WhatsAppChat from "./layouts/profile/components/WhatsAppChat";
import StudentPortalRequest from "./layouts/authentication/components/StudentPortalRequest";
export default function App() {
  const [controller, dispatch] = useMaterialUIController();
  const {
    miniSidenav,
    direction,
    layout,
    openConfigurator,
    sidenavColor,
    transparentSidenav,
    whiteSidenav,
    darkMode,
  } = controller;

  const [onMouseEnter, setOnMouseEnter] = useState(false);
  const [rtlCache, setRtlCache] = useState(null);
  const { pathname } = useLocation();

  // RTL Cache setup
  useMemo(() => {
    const cacheRtl = createCache({
      key: "rtl",
      stylisPlugins: [rtlPlugin],
    });

    setRtlCache(cacheRtl);
  }, []);

  // Sidenav mouse handlers
  const handleOnMouseEnter = () => {
    if (miniSidenav && !onMouseEnter) {
      setMiniSidenav(dispatch, false);
      setOnMouseEnter(true);
    }
  };

  const handleOnMouseLeave = () => {
    if (onMouseEnter) {
      setMiniSidenav(dispatch, true);
      setOnMouseEnter(false);
    }
  };

  const handleConfiguratorOpen = () => setOpenConfigurator(dispatch, !openConfigurator);

  // Handle RTL direction
  useEffect(() => {
    document.body.setAttribute("dir", direction);
  }, [direction]);

  // Scroll to top on route change
  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
  }, [pathname]);

  // Initialize global error handlers
  useEffect(() => {
    initializeGlobalErrorHandlers();
  }, []);

  // Route rendering logic
  const getRoutes = (allRoutes) =>
    allRoutes.flatMap((route) => {
      if (route.collapse) {
        return getRoutes(route.collapse);
      }

      if (route.route && route.component) {
        return (
          <Route
            exact
            path={route.route}
            element={route.component}
            key={route.key || route.route}
          />
        );
      }

      return [];
    });

  const configsButton = (
    <MDBox
      display="flex"
      justifyContent="center"
      alignItems="center"
      width="3.25rem"
      height="3.25rem"
      bgColor="white"
      shadow="sm"
      borderRadius="50%"
      position="fixed"
      right="2rem"
      bottom="2rem"
      zIndex={99}
      color="dark"
      sx={{ cursor: "pointer" }}
      onClick={handleConfiguratorOpen}
    >
      <Icon fontSize="small" color="inherit">
        settings
      </Icon>
    </MDBox>
  );

  const content = (
    <>
      <CssBaseline />
      {layout === "dashboard" && (
        <>
          <Sidenav
            color={sidenavColor}
            brand={(transparentSidenav && !darkMode) || whiteSidenav ? brandDark : brandWhite}
            brandName="Material Dashboard 2"
            routes={routes}
            onMouseEnter={handleOnMouseEnter}
            onMouseLeave={handleOnMouseLeave}
          />
          <Configurator />
          {configsButton}
          <SafeToastContainer
            position="top-right"
            autoClose={2000}
            hideProgressBar={false}
            newestOnTop={false}
            closeOnClick
            rtl={false}
            pauseOnFocusLoss
            draggable
            pauseOnHover
            theme={darkMode ? "dark" : "light"}
            enableMultiContainer={false}
            limit={5}
            closeButton={true}
          />
        </>
      )}
      {layout === "vr" && <Configurator />}
      <Routes>
        {getRoutes(routes)}
        <Route path="*" element={<Navigate to="/authentication/sign-in" />} />
        <Route path="/coming-soon" element={<ComingSoon />} />
        <Route path="/profile" element={<Profile />} />
        <Route path="/authentication/sign-in" element={<SignIn />} />
        <Route path="/authentication/sign-up" element={<SignUp />} />
        <Route path="/BasicAuth" element={<CheckingAuth />} />
        <Route path="/BasicDetails" element={<BasicDetails />} />
        <Route path="/DistricWiseStudent" element={<DistrictStudentMap />} />
        <Route path="/chapter" element={<ChapterDetails />} />
        <Route path="/AdminAuth" element={<AdminAuth />} />
        <Route path="/AdminLogin" element={<AdminLogin />} />
        <Route path="/AdminRegister" element={<AdminRegister />} />
        <Route path="/AdminHome" element={<AdminHome />} />
        <Route path="/AdminChapter" element={<AdminChapterManager />} />
        <Route path="/linkedin-callback" element={<LinkedInIntegration />} />
        <Route path="/GlobalChat" element={<GlobalChat />} />
        <Route path="/Chat" element={<WhatsAppChat />} />
        <Route path="/student-portal-request" element={<StudentPortalRequest />} />

      </Routes>
      
      {/* AI Chatbot Components - Hidden on admin pages */}
      {!pathname.startsWith("/Admin") && !pathname.startsWith("/admin") && (
        <>
          <ChatbotButton />
          <SimpleAIChatbot />
        </>
      )}
    </>
  );

  return (
    <ErrorBoundary>
      {direction === "rtl" ? (
        <CacheProvider value={rtlCache}>
          <ThemeProvider theme={darkMode ? themeDarkRTL : themeRTL}>
            <ChatbotProvider>
              {content}
            </ChatbotProvider>
          </ThemeProvider>
        </CacheProvider>
      ) : (
        <ThemeProvider theme={darkMode ? themeDark : theme}>
          <ChatbotProvider>
            {content}
          </ChatbotProvider>
        </ThemeProvider>
      )}
    </ErrorBoundary>
  );
}
